import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Button, Modal, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useSelector } from 'react-redux';

import { logoutUser } from 'src/routes/AuthContext';
import type { EmploymentStatusProfile } from 'src/types/employment-status';
import {
  DEFAULT_LOCKED_OUT_MESSAGE,
  LOCKED_OUT_EMPLOYMENT_STATUS,
} from 'src/types/employment-status';

/**
 * APIT-622 — blocks the portal when Compliance has manually locked the employee out.
 *
 * Follows the licensure-expiry blocker in `vertical-layout.tsx` (non dismissable overlay
 * with a plain message card), but is mounted on the shared dashboard layout so it applies
 * platform wide — every route, every role and every facility. There is deliberately no
 * close button, no escape key and no backdrop click handler: the only way out is the
 * Log Out button below, or the automatic logout that follows it.
 */

/**
 * Per product: the employee reads the message, then either logs out themselves or is
 * signed out for them. Matches the 60s countdown the inactivity dialog already uses.
 */
const AUTO_LOGOUT_SECONDS = 60;

export const EmploymentLockoutModal: FC = () => {
  const therapistProfile = useSelector((state: any) => state.getTherapistProfileReducer);
  const profile: Partial<EmploymentStatusProfile> | undefined = therapistProfile?.response?.data;

  // No profile yet (first load, or the reducer was reset on facility switch) means there is
  // nothing to judge, so stay out of the way. Deliberately NOT gated on `loader`: the
  // persisted profile survives a page reload and routes.tsx immediately re-fetches it, so
  // gating on `loader` would lift the block for the whole round trip on every refresh.
  const isLockedOut =
    Boolean(profile) && profile?.employmentStatus === LOCKED_OUT_EMPLOYMENT_STATUS;

  const [secondsLeft, setSecondsLeft] = useState(AUTO_LOGOUT_SECONDS);

  // Hooks run on every render, so the lockout check lives inside rather than behind an
  // early return. The timeout is what actually logs out; the interval only drives the
  // visible count, which keeps the two from racing and double-calling logoutUser.
  useEffect(() => {
    if (!isLockedOut) {
      setSecondsLeft(AUTO_LOGOUT_SECONDS);
      return undefined;
    }

    const timeout = setTimeout(logoutUser, AUTO_LOGOUT_SECONDS * 1000);
    const interval = setInterval(() => {
      setSecondsLeft((remaining) => (remaining > 0 ? remaining - 1 : 0));
    }, 1000);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [isLockedOut]);

  if (!isLockedOut) {
    return null;
  }

  const message = profile?.employmentStatusMessage?.trim() || DEFAULT_LOCKED_OUT_MESSAGE;

  return (
    <Modal
      open
      disableEscapeKeyDown
      aria-labelledby="employment-lockout-title"
      aria-describedby="employment-lockout-message"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(6px)',
          },
        },
      }}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'background.paper',
          borderRadius: '8px',
          boxShadow: 24,
          outline: 'none',
          padding: { xs: '16px', sm: '20px' },
          width: '90%',
          maxWidth: '520px',
        }}
      >
        <Typography
          id="employment-lockout-title"
          variant="h5"
          sx={{ marginBottom: '20px' }}
        >
          Account Access Suspended
        </Typography>
        <Typography id="employment-lockout-message">{message}</Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ marginTop: '12px' }}
        >
          You will be logged out automatically in {secondsLeft}{' '}
          {secondsLeft === 1 ? 'second' : 'seconds'}.
        </Typography>
        <Stack
          direction="row"
          justifyContent="flex-end"
          sx={{ marginTop: '20px' }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={logoutUser}
          >
            Log Out
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};
